import { useEffect, useMemo, useState } from "react";
import type { RegionInfo } from "../services/regionService";
import {
  buildConsent,
  consumeCookieSettingsOpenFlag,
  COOKIE_SETTINGS_EVENT,
  getDefaultConsent,
  getStoredConsent,
  setStoredConsent,
  shouldShowConsentBanner,
  type CookieConsent,
} from "../services/cookieConsentService";

type CookieConsentBannerProps = {
  regionInfo: RegionInfo | null;
  onConsentUpdated: (consent: CookieConsent | null) => void;
};

export default function CookieConsentBanner({
  regionInfo,
  onConsentUpdated,
}: CookieConsentBannerProps) {
  const [consent, setConsent] = useState<CookieConsent | null>(
    getStoredConsent()
  );
  const [isForcedOpen, setIsForcedOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const defaults = useMemo(
    () =>
      getDefaultConsent(
        regionInfo?.regionBucket ?? "UNKNOWN"
      ),
    [regionInfo?.regionBucket]
  );
  const [analyticsEnabled, setAnalyticsEnabled] = useState(defaults.analytics);
  const [marketingEnabled, setMarketingEnabled] = useState(defaults.marketing);

  useEffect(() => {
    const openIfRequested = () => {
      if (consumeCookieSettingsOpenFlag()) {
        setAnalyticsEnabled(consent?.analytics ?? defaults.analytics);
        setMarketingEnabled(consent?.marketing ?? defaults.marketing);
        setShowPreferences(true);
        setIsForcedOpen(true);
      }
    };
    openIfRequested();
    window.addEventListener(COOKIE_SETTINGS_EVENT, openIfRequested);
    return () => {
      window.removeEventListener(COOKIE_SETTINGS_EVENT, openIfRequested);
    };
  }, [consent, defaults.analytics, defaults.marketing]);

  const isOpen =
    isForcedOpen || shouldShowConsentBanner(regionInfo?.regionBucket, consent);

  const openPreferences = () => {
    setAnalyticsEnabled(consent?.analytics ?? defaults.analytics);
    setMarketingEnabled(consent?.marketing ?? defaults.marketing);
    setShowPreferences(true);
    setIsForcedOpen(true);
  };

  const closeBanner = () => {
    setIsForcedOpen(false);
    setShowPreferences(false);
  };

  if (!isOpen || !regionInfo) {
    return null;
  }

  const isEuRegion =
    regionInfo.regionBucket === "EU" ||
    regionInfo.regionBucket === "UK" ||
    regionInfo.regionBucket === "UNKNOWN";
  const isUsOptOut = regionInfo.regionBucket === "US_OPT_OUT";

  const handleAcceptAll = () => {
    const next = buildConsent(true, true);
    setStoredConsent(next);
    setConsent(next);
    onConsentUpdated(next);
    closeBanner();
  };

  const handleRejectNonEssential = () => {
    const next = buildConsent(false, false);
    setStoredConsent(next);
    setConsent(next);
    onConsentUpdated(next);
    closeBanner();
  };

  const handleDismiss = () => {
    const next = buildConsent(defaults.analytics, defaults.marketing);
    setStoredConsent(next);
    setConsent(next);
    onConsentUpdated(next);
    closeBanner();
  };

  const handleDoNotSell = () => {
    const next = buildConsent(defaults.analytics, false);
    setStoredConsent(next);
    setConsent(next);
    onConsentUpdated(next);
    closeBanner();
  };

  const handleSavePreferences = () => {
    const next = buildConsent(analyticsEnabled, marketingEnabled);
    setStoredConsent(next);
    setConsent(next);
    onConsentUpdated(next);
    closeBanner();
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 text-white border-t border-zinc-800 px-6 py-4">
      <div className="max-w-5xl mx-auto flex flex-col gap-4">
        <div className="text-sm text-zinc-200">
          We use essential cookies to keep the site working. With your consent,
          we use analytics and marketing cookies to improve the service.
        </div>

        {showPreferences ? (
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                className="h-4 w-4"
              />
              Analytics cookies
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={marketingEnabled}
                onChange={(event) => setMarketingEnabled(event.target.checked)}
                className="h-4 w-4"
              />
              Marketing cookies
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wide"
              >
                Save preferences
              </button>
              <button
                onClick={() => setShowPreferences(false)}
                className="px-4 py-2 rounded-full border border-zinc-600 text-xs font-semibold uppercase tracking-wide"
              >
                Back
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {isEuRegion && (
              <>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wide"
                >
                  Accept all
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="px-4 py-2 rounded-full border border-zinc-600 text-xs font-semibold uppercase tracking-wide"
                >
                  Reject non-essential
                </button>
                <button
                  onClick={openPreferences}
                  className="px-4 py-2 rounded-full border border-zinc-600 text-xs font-semibold uppercase tracking-wide"
                >
                  Cookie settings
                </button>
              </>
            )}
            {isUsOptOut && (
              <>
                <button
                  onClick={handleDoNotSell}
                  className="px-4 py-2 rounded-full border border-zinc-600 text-xs font-semibold uppercase tracking-wide"
                >
                  Do Not Sell/Share
                </button>
                <button
                  onClick={openPreferences}
                  className="px-4 py-2 rounded-full border border-zinc-600 text-xs font-semibold uppercase tracking-wide"
                >
                  Cookie settings
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wide"
                >
                  Dismiss
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
